"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Info } from "lucide-react";

/**
 * Click-toggled popover explaining special (non-standard) builds. Same
 * interaction pattern as GaugeInfo: toggle button + Escape / outside-click
 * to dismiss, so it works on touch (hover-only tooltips don't).
 */
export function NonStandardInfo() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className="relative inline-flex">
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label="What does a special build mean?"
        className="inline-flex h-6 w-6 items-center justify-center rounded-full border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
        style={{ borderColor: "#2A2A2A", color: "#E8C547" }}
      >
        <Info size={13} aria-hidden />
      </button>
      {open && (
        <span
          id={panelId}
          role="tooltip"
          className="absolute left-1/2 top-8 z-20 w-64 -translate-x-1/2 rounded-sm border px-3 py-2 text-xs shadow-xl"
          style={{ backgroundColor: "#141414", borderColor: "#E8C54755", color: "#F5F5F5" }}
        >
          Marked counts are special builds — our builder gives them a quick
          review before production. No extra action needed.
        </span>
      )}
    </span>
  );
}
